import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { getExecution, ExecutionWithId } from '../services/firestore-proxy'
import { DynamicUIRenderer } from '../components/DynamicUIRenderer'
import { BrCard, BrButton } from '@govbr-ds/react-components'

/**
 * PreviewPage - Opens execution UI in full screen for detailed viewing
 */
export function PreviewPage() {
    const { executionId } = useParams<{ executionId: string }>()
    const [execution, setExecution] = useState<ExecutionWithId | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (executionId) {
            loadExecution(executionId)
        }
    }, [executionId])

    const loadExecution = async (id: string) => {
        try {
            const result = await getExecution(id)
            if (result.success && result.data) {
                setExecution(result.data)
            } else {
                setError('Execução não encontrada')
            }
        } catch (err) {
            console.error('Error loading execution:', err)
            setError('Erro ao carregar execução')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen d-flex align-items-center justify-content-center bg-gray-2">
                <Spinner />
            </div>
        )
    }

    if (error || !execution) {
        return (
            <div className="min-h-screen d-flex align-items-center justify-content-center bg-gray-2">
                <div className="text-center">
                    <p className="text-danger text-lg">{error || 'Execução não encontrada'}</p>
                    <BrButton
                        secondary
                        onClick={() => window.close()}
                        className="mt-4"
                    >
                        Fechar janela
                    </BrButton>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-2 pb-5">
            {/* Header */}
            <div className="bg-pure-0 shadow-sm border-b border-gray-20 px-6 py-4 mb-4">
                <div className="max-w-5xl mx-auto d-flex flex-column md:flex-row justify-content-between align-items-center gap-3">
                    <div>
                        <h1 className="text-xl font-bold text-gray-80 m-0">
                            Pré-visualização da Interface
                        </h1>
                        <p className="text-sm text-gray-60 m-0">
                            ID: {execution.case_id || execution.execution_id}
                        </p>
                    </div>
                    <div className="d-flex flex-wrap gap-4 text-sm text-gray-60">
                        <span><strong>Perfil:</strong> {execution.profile_id || 'N/A'}</span>
                        <span><strong>Tarefa:</strong> {execution.task_type || 'N/A'}</span>
                        <span><strong>Fase:</strong> {execution.process_phase || 'N/A'}</span>
                        {(execution.tads || execution.metrics?.tads) && (
                            <span><strong>TADS:</strong> {(execution.tads || execution.metrics?.tads)?.toFixed(1)}%</span>
                        )}
                    </div>
                </div>
            </div>

            {/* UI Content */}
            <div className="container-lg">
                <div className="row justify-content-center">
                    <div className="col-12 col-xl-10">
                        <BrCard>
                            <div className="p-4">
                                {execution.ui_spec ? (
                                    <DynamicUIRenderer uiSpec={execution.ui_spec} />
                                ) : (
                                    <p className="text-gray-60 text-center py-8">
                                        Nenhuma interface gerada disponível
                                    </p>
                                )}
                            </div>
                        </BrCard>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="fixed bottom-0 left-0 right-0 bg-pure-0 border-t border-gray-20 px-6 py-3">
                <div className="max-w-5xl mx-auto d-flex justify-content-end">
                    <BrButton
                        secondary
                        onClick={() => window.close()}
                        size="small"
                    >
                        Fechar janela
                    </BrButton>
                </div>
            </div>
        </div>
    )
}

function Spinner() {
    return (
        <div className="loading mr-1"></div>
    )
}
